/**
 * AnalyticsHub — Sprint V2.G8.
 *
 * Single-page wrapper that aggregates the 6 analytics destinations
 * (Overview / Performance / Risk / Trading / Positions / Insights)
 * behind one `<MonitorTabs>` row, per V2.G3 Section C uniformisation
 * proposal.
 *
 * The 6 standalone TabIds (`analytics-overview` etc.) survive — each
 * activates this hub with the matching pane visible. Switching tabs
 * triggers a real `setActiveTab` in the parent App, so the URL stays
 * in sync (bookmarks on /analytics/performance still work).
 *
 * Sidebar consolidation : the 6 child entries collapse into a single
 * "Analytics" parent entry that defaults to overview. Power users can
 * still type the deep URL directly.
 */

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../components/Icons';
import { MonitorTabs, type MonitorTab } from '../../components/monitor/MonitorTabs';
import { DataStateCard } from '../../components/ui/DataStateCard';
import styles from './AnalyticsHub.module.css';

// Lazy-loaded children — consistent with the pre-existing pattern in
// PortfolioDashboard for sub-pages.
const PortfolioOverview = lazy(() =>
  import('./PortfolioOverview').then(m => ({ default: m.PortfolioOverview })),
);
const PerformanceAnalytics = lazy(() =>
  import('./PerformanceAnalytics').then(m => ({ default: m.PerformanceAnalytics })),
);
const RiskAnalysis = lazy(() =>
  import('./RiskAnalysis').then(m => ({ default: m.RiskAnalysis })),
);
const TradingTools = lazy(() =>
  import('./TradingTools').then(m => ({ default: m.TradingTools })),
);
const PositionManagement = lazy(() =>
  import('./PositionManagement').then(m => ({ default: m.PositionManagement })),
);
const Insights = lazy(() =>
  import('./Insights').then(m => ({ default: m.Insights })),
);

export type AnalyticsTabId =
  | 'analytics-overview'
  | 'analytics-performance'
  | 'analytics-risk'
  | 'analytics-trading'
  | 'analytics-positions'
  | 'analytics-insights';

interface AnalyticsHubProps {
  /** The currently-activated TabId, sourced from the parent App. */
  activeTab: AnalyticsTabId;
  /** Bubbles a tab change up to App so the URL + activeTab state update. */
  onTabChange: (id: AnalyticsTabId) => void;
}

// Analytics-themed palette to drive `<MonitorTabs>` (it's typed against
// `NetworkColors` because that's what the per-chain monitors use; we
// reuse it here with neutral analytics tones).
const ANALYTICS_COLORS = {
  primary: 'var(--accent)',
  gradient: 'linear-gradient(135deg, var(--accent), var(--accent))',
  lightGradient:
    'linear-gradient(135deg, color-mix(in srgb, var(--accent) 18%, transparent), transparent)',
};

const ChildFallback = () => (
  <DataStateCard
    variant="loading"
    icon={<Icons.Activity size={48} />}
    message="Chargement…"
  />
);

export function AnalyticsHub({ activeTab, onTabChange }: AnalyticsHubProps) {
  const { t } = useTranslation();

  const tabs: MonitorTab<AnalyticsTabId>[] = [
    {
      id: 'analytics-overview',
      label: t('analyticsHub.overview', 'Vue d\'ensemble'),
      icon: <Icons.PieChart size={16} />,
    },
    {
      id: 'analytics-performance',
      label: t('analyticsHub.performance', 'Performance'),
      icon: <Icons.TrendingUp size={16} />,
    },
    {
      id: 'analytics-risk',
      label: t('analyticsHub.risk', 'Risque'),
      icon: <Icons.Shield size={16} />,
    },
    {
      id: 'analytics-trading',
      label: t('analyticsHub.trading', 'Trading'),
      icon: <Icons.Activity size={16} />,
    },
    {
      id: 'analytics-positions',
      label: t('analyticsHub.positions', 'Positions'),
      icon: <Icons.Target size={16} />,
    },
    {
      id: 'analytics-insights',
      label: t('analyticsHub.insights', 'Insights'),
      icon: <Icons.Activity size={16} />,
    },
  ];

  return (
    <div className={styles.root}>
      <MonitorTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={onTabChange}
        colors={ANALYTICS_COLORS}
        ariaLabel={t('analyticsHub.tablistLabel', 'Navigation Analytics')}
      />

      <Suspense fallback={<ChildFallback />}>
        {activeTab === 'analytics-overview' && <PortfolioOverview />}
        {activeTab === 'analytics-performance' && <PerformanceAnalytics />}
        {activeTab === 'analytics-risk' && <RiskAnalysis />}
        {activeTab === 'analytics-trading' && <TradingTools />}
        {activeTab === 'analytics-positions' && <PositionManagement />}
        {activeTab === 'analytics-insights' && <Insights />}
      </Suspense>
    </div>
  );
}
